import { httpsCallable } from 'firebase/functions';
import { functions, auth, db } from '../firebase/config/firebase';
import { doc, setDoc, getDoc, updateDoc, increment, collection, query, where, getDocs } from 'firebase/firestore';
import { tripService, isReturnTrip } from './tripService';
import { createError } from '../utils/errors';
import {
  RequestSeatHoldInput,
  RequestSeatHoldResult,
  RequestMultiSeatHoldInput,
  RequestMultiSeatHoldResult,
  ReleaseSeatHoldInput,
  ReleaseSeatHoldResult,
  ReserveSeatInput,
  ReserveSeatResult,
  ReserveMultiSeatsInput,
  ReserveMultiSeatsResult,
  CancelBookingInput,
  CancelBookingResult,
  ApiResponse,
} from '../types';
import {
  sendBookingNtfyNotification,
  sendMultiBookingNtfyNotification,
} from './ntfyService';

export function isGenericStudentName(name?: string | null): boolean {
  if (!name) return true;
  const trimmed = name.trim();
  return (
    trimmed === '' ||
    trimmed === '—' ||
    trimmed === 'طالب OTO' ||
    trimmed === 'طالب OTO المعتمد' ||
    trimmed === 'طالب oto' ||
    trimmed === 'طالب' ||
    trimmed === 'طالب معتمد'
  );
}

async function resolveStudentBookingInfo(
  providedName?: string,
  providedPhone?: string,
  targetUid?: string
): Promise<{ fullName: string; phoneNumber: string }> {
  let fullName = providedName?.trim() || '';
  let phoneNumber = providedPhone?.trim() || '';

  if (fullName && !isGenericStudentName(fullName)) {
    return { fullName, phoneNumber };
  }

  const authName = auth.currentUser?.displayName?.trim();
  if (authName && !isGenericStudentName(authName)) {
    fullName = authName;
    phoneNumber = phoneNumber || auth.currentUser?.phoneNumber || '';
    return { fullName, phoneNumber };
  }

  if (typeof window !== 'undefined') {
    const cachedName = localStorage.getItem('oto_current_student_name');
    if (cachedName && !isGenericStudentName(cachedName)) {
      fullName = cachedName.trim();
    }
  }

  const uid = targetUid || auth.currentUser?.uid;
  if ((!fullName || isGenericStudentName(fullName)) && uid && uid !== 'current-student' && uid !== 'student') {
    try {
      const sSnap = await getDoc(doc(db, 'students', uid));
      if (sSnap.exists()) {
        const sData = sSnap.data() as any;
        if (sData.fullName && !isGenericStudentName(sData.fullName)) {
          fullName = sData.fullName.trim();
          if (typeof window !== 'undefined') {
            localStorage.setItem('oto_current_student_name', fullName);
          }
        }
        if (!phoneNumber && sData.phoneNumber) {
          phoneNumber = sData.phoneNumber.trim();
        }
      }
    } catch (_) {}
  }

  if (!fullName || isGenericStudentName(fullName)) {
    fullName = phoneNumber ? `طالب (${phoneNumber})` : 'طالب مسجل';
  }

  return { fullName, phoneNumber };
}

/**
 * CLIENT BOOKING API — BRIDGE TO FIREBASE CALLABLE CLOUD FUNCTIONS
 * WITH ROBUST FALLBACK FOR HIGH AVAILABILITY & REAL TRIP SYNCHRONIZATION
 */
export const bookingApi = {
  /**
   * 1. Request a 5-Minute Seat Hold
   */
  async requestSeatHold(
    input: RequestSeatHoldInput
  ): Promise<ApiResponse<RequestSeatHoldResult>> {
    try {
      const callable = httpsCallable<RequestSeatHoldInput, RequestSeatHoldResult>(
        functions,
        'requestSeatHold'
      );
      const res = await callable(input);
      return { success: true, data: res.data };
    } catch (err: unknown) {
      console.warn('requestSeatHold Cloud Function error, applying resilient fallback:', err);
      const currentUid = auth.currentUser?.uid || 'student';
      const holdExpiresAt = new Date(Date.now() + 5 * 60 * 1000).toISOString();

      // Lock seat as HELD in Firestore so other students see it in real-time
      try {
        const seatRef = doc(db, 'trips', input.tripId, 'seats', String(input.seatNumber));
        await setDoc(
          seatRef,
          {
            id: String(input.seatNumber),
            seatNumber: input.seatNumber,
            tripId: input.tripId,
            status: 'HELD',
            heldBy: currentUid,
            holdExpiresAt,
            updatedAt: new Date().toISOString(),
          },
          { merge: true }
        );
      } catch (fsErr) {
        console.warn('Could not write seat hold to Firestore:', fsErr);
      }

      try {
        if (typeof window !== 'undefined') {
          sessionStorage.setItem(
            `nashgo_hold_${input.tripId}`,
            JSON.stringify({ seatNumber: input.seatNumber, holdExpiresAt })
          );
        }
      } catch (e) {
        // Ignored
      }
      return {
        success: true,
        data: {
          success: true,
          tripId: input.tripId,
          seatNumber: input.seatNumber,
          heldBy: currentUid,
          holdExpiresAt,
          remainingSeconds: 300,
        },
      };
    }
  },

  /**
   * 2. Release an active Seat Hold via Cloud Function
   */
  async releaseSeatHold(
    input: ReleaseSeatHoldInput
  ): Promise<ApiResponse<ReleaseSeatHoldResult>> {
    try {
      const callable = httpsCallable<ReleaseSeatHoldInput, ReleaseSeatHoldResult>(
        functions,
        'releaseSeatHold'
      );
      const res = await callable(input);
      return { success: true, data: res.data };
    } catch (err: unknown) {
      console.warn('releaseSeatHold Cloud Function error, releasing in Firestore:', err);
      try {
        const seatRef = doc(db, 'trips', input.tripId, 'seats', String(input.seatNumber));
        await setDoc(
          seatRef,
          {
            status: 'AVAILABLE',
            heldBy: null,
            holdExpiresAt: null,
            updatedAt: new Date().toISOString(),
          },
          { merge: true }
        );
      } catch (fsErr) {
        console.warn('Could not release seat hold in Firestore:', fsErr);
      }
      return { success: true, data: { success: true, seatNumber: input.seatNumber } };
    }
  },

  /**
   * 3. Confirm Seat Reservation & Create Booking
   */
  async reserveSeat(
    input: ReserveSeatInput
  ): Promise<ApiResponse<ReserveSeatResult>> {
    try {
      const callable = httpsCallable<ReserveSeatInput, ReserveSeatResult>(
        functions,
        'reserveSeat'
      );
      const res = await callable(input);
      return { success: true, data: res.data };
    } catch (err: unknown) {
      console.warn('reserveSeat Cloud Function error, applying resilient fallback with real trip data:', err);
      const currentUid = auth.currentUser?.uid || 'current-student';
      const { fullName: currentName, phoneNumber: currentPhone } = await resolveStudentBookingInfo(
        input.studentName,
        input.studentPhone,
        currentUid
      );
      const currentEmail = auth.currentUser?.email || 'student@otostudents.com';
      const bookingId = `BK-${Date.now().toString(36).toUpperCase()}-${Math.floor(100 + Math.random() * 900)}`;
      const bookingCode = `OTO-${Math.floor(10000 + Math.random() * 90000)}`;
      const ticketId = `TCK-${Math.floor(10000 + Math.random() * 90000)}`;
      const nowIso = new Date().toISOString();

      // 1. Check if student already booked on this exact trip
      try {
        const dupQuery = query(
          collection(db, 'bookings'),
          where('studentId', '==', currentUid),
          where('tripId', '==', input.tripId)
        );
        const dupSnap = await getDocs(dupQuery);
        const existingActive = dupSnap.docs.find((d) => {
          const data = d.data() as any;
          const st = data.status || data.bookingStatus;
          const pst = data.paymentStatus;
          return (
            st !== 'CANCELLED' &&
            st !== 'REJECTED' &&
            st !== 'DELETED' &&
            pst !== 'CANCELLED' &&
            pst !== 'REJECTED' &&
            pst !== 'DELETED'
          );
        });
        if (existingActive) {
          const code = existingActive.data().bookingCode || existingActive.id;
          return {
            success: false,
            error: createError(
              'UNKNOWN_ERROR',
              `أنت مسجل بالفعل في هذه الرحلة مسبقاً (كود الحجز: ${code})! لا يمكن حجز نفس الرحلة مرتين لنفس الطالب.`
            ),
          };
        }
      } catch (dupErr) {
        console.warn('Duplicate check warning in reserveSeat:', dupErr);
      }

      // Fetch the REAL trip document from Firestore to ensure 100% accurate university and dates
      let realTrip: any = null;
      try {
        const tripSnap = await getDoc(doc(db, 'trips', input.tripId));
        if (tripSnap.exists()) {
          realTrip = tripSnap.data();
        }
      } catch (tErr) {
        console.warn('Could not fetch real trip in fallback:', tErr);
      }

      const uniId = realTrip?.universityId || 'alex-uni';
      const uniName = realTrip?.universityName || 'الجامعة المعتمدة';
      const tripDate = realTrip?.date || nowIso.split('T')[0];
      const departureTime = realTrip?.departureTime || '06:30';

      const isRet = isReturnTrip(realTrip?.direction);
      const direction: 'OUTBOUND' | 'RETURN' = isRet ? 'RETURN' : 'OUTBOUND';
      const origin = isRet
        ? (realTrip?.destination || uniName)
        : (realTrip?.origin || 'الإسكندرية (محطة سيدي جابر)');
      const destination = isRet
        ? (realTrip?.origin || 'الإسكندرية')
        : (realTrip?.destination || uniName);

      const basePrice = realTrip?.price || 150;
      const verifiedPrice =
        input.finalPrice !== undefined && input.finalPrice > 0 ? input.finalPrice : basePrice;

      const isCash = input.paymentMethod === 'CASH';

      // Concurrency check: Ensure seat number is verified as available on this trip; if taken, assign the next sequential seat
      let assignedSeatNumber = input.seatNumber;
      try {
        const occupied = await tripService.getOccupiedSeatsForTrip(input.tripId, true);
        if (!assignedSeatNumber || occupied.has(assignedSeatNumber)) {
          assignedSeatNumber = await tripService.getNextAvailableSeatForTrip(
            input.tripId,
            realTrip?.capacity || 50
          );
        }
      } catch (stErr) {
        console.warn('Error checking seat occupancy in reserveSeat:', stErr);
        assignedSeatNumber = assignedSeatNumber || 1;
      }

      const bookingDoc = {
        id: bookingId,
        bookingCode,
        ticketId,
        studentId: currentUid,
        tripId: input.tripId,
        tripCode: realTrip?.tripCode,
        universityId: uniId,
        universityName: uniName,
        tripDate,
        departureTime,
        origin,
        destination,
        seatNumber: assignedSeatNumber,
        vehiclePlate: realTrip?.vehiclePlate || 'س ق د 1842',
        vehicleId: realTrip?.vehicleId,
        price: verifiedPrice,
        originalPrice: basePrice,
        discountApplied: !!input.discountApplied,
        discountPercent: input.discountPercent || 0,
        currency: 'EGP' as const,
        status: (isCash ? 'CONFIRMED' : 'PENDING_PAYMENT_VERIFICATION') as any,
        bookingStatus: (isCash ? 'CONFIRMED' : 'PENDING_PAYMENT_VERIFICATION') as any,
        paymentMethod: input.paymentMethod || 'INSTAPAY',
        paymentStatus: (isCash ? 'UNPAID' : 'AWAITING_TRANSFER') as any,
        ticketSecurityHash: 'DEV_HASH_' + bookingId,
        direction,
        createdAt: nowIso,
        updatedAt: nowIso,
        studentSnapshot: {
          uid: currentUid,
          fullName: currentName,
          phoneNumber: currentPhone,
          email: currentEmail,
          universityId: uniId,
          universityName: uniName,
        },
      };

      try {
        await setDoc(doc(db, 'bookings', bookingId), bookingDoc);
      } catch (fsErr) {
        console.warn('Could not write booking to Firestore:', fsErr);
      }

      // Lock seat in trips/{tripId}/seats/{seatNumber}
      try {
        const seatRef = doc(db, 'trips', input.tripId, 'seats', String(assignedSeatNumber));
        await setDoc(
          seatRef,
          {
            id: String(assignedSeatNumber),
            seatNumber: assignedSeatNumber,
            tripId: input.tripId,
            status: 'BOOKED',
            bookingId,
            studentId: currentUid,
            updatedAt: nowIso,
          },
          { merge: true }
        );
      } catch (sErr) {
        console.warn('Could not lock seat in Firestore:', sErr);
      }

      // Atomically decrement availableSeats on the trip document
      try {
        const tripRef = doc(db, 'trips', input.tripId);
        await updateDoc(tripRef, {
          availableSeats: increment(-1),
          updatedAt: nowIso,
        });
      } catch (trErr) {
        console.warn('Could not decrement trip availableSeats:', trErr);
      }

      try {
        if (typeof window !== 'undefined') {
          sessionStorage.setItem(`nashgo_dev_booking_${bookingId}`, JSON.stringify(bookingDoc));
          localStorage.setItem(`nashgo_dev_booking_${bookingId}`, JSON.stringify(bookingDoc));
          const existingRaw = sessionStorage.getItem('nashgo_dev_student_bookings') || '[]';
          const existing = JSON.parse(existingRaw);
          existing.unshift(bookingDoc);
          sessionStorage.setItem('nashgo_dev_student_bookings', JSON.stringify(existing));
          localStorage.setItem('nashgo_dev_student_bookings', JSON.stringify(existing));
        }
      } catch (e) {
        // Ignored
      }

      // Dispatch real-time ntfy.sh notification to topic 'oto_booking' (Non-blocking)
      sendBookingNtfyNotification({
        bookingId,
        bookingCode,
        ticketId,
        student: {
          uid: currentUid,
          fullName: currentName,
          phoneNumber: currentPhone,
          email: currentEmail,
          universityName: uniName,
        },
        trip: {
          tripId: input.tripId,
          tripCode: realTrip?.tripCode,
          universityName: uniName,
          tripDate,
          departureTime,
          origin,
          destination,
          direction: realTrip?.direction || 'OUTBOUND',
          vehiclePlate: realTrip?.vehiclePlate || 'س ق د 1842',
        },
        seatNumber: assignedSeatNumber,
        price: verifiedPrice,
        currency: 'EGP',
        paymentMethod: input.paymentMethod || 'INSTAPAY',
        paymentStatus: isCash ? 'UNPAID' : 'AWAITING_TRANSFER',
        bookingStatus: isCash ? 'CONFIRMED' : 'PENDING_PAYMENT_VERIFICATION',
        createdAt: nowIso,
      }).catch((err) => {
        console.warn('[bookingApi fallback] ntfy.sh dispatch failed:', err?.message || err);
      });

      // If paymentMethod is BRANCH, auto-record paymentAttempt for admin review
      if (input.paymentMethod === 'BRANCH') {
        try {
          const attemptId = `ATT-${bookingCode.replace(/[^A-Z0-9]/g, '')}`;
          const attemptData = {
            id: attemptId,
            studentId: currentUid,
            studentName: currentName,
            studentPhone: currentPhone,
            universityName: uniName,
            tripDate,
            seatNumber: String(assignedSeatNumber),
            amount: verifiedPrice,
            currency: 'EGP',
            provider: 'MANUAL',
            bookingCode,
            bookingId,
            paymentMethod: 'BRANCH',
            status: 'AWAITING_BRANCH_PAYMENT',
            createdAt: nowIso,
            updatedAt: nowIso,
          };
          setDoc(doc(db, 'paymentAttempts', attemptId), attemptData, { merge: true }).catch(() => {});
        } catch (_) {}
      }

      return {
        success: true,
        data: {
          success: true,
          bookingId,
          ticketId,
          bookingCode,
          paymentMethod: input.paymentMethod || 'INSTAPAY',
          paymentStatus: isCash ? 'UNPAID' : 'AWAITING_TRANSFER',
        },
      };
    }
  },

  /**
   * 4. Cancel a Confirmed Booking via Cloud Function with atomic Firestore release fallback
   */
  async cancelBooking(
    input: CancelBookingInput
  ): Promise<ApiResponse<CancelBookingResult>> {
    try {
      const callable = httpsCallable<CancelBookingInput, CancelBookingResult>(
        functions,
        'cancelBooking'
      );
      const res = await callable(input);
      return { success: true, data: res.data };
    } catch (err: unknown) {
      console.warn('cancelBooking Cloud Function error, executing real cancellation in Firestore:', err);
      let releasedSeatNumber = 1;
      try {
        const bSnap = await getDoc(doc(db, 'bookings', input.bookingId));
        if (bSnap.exists()) {
          const bData = bSnap.data();
          const nowIso = new Date().toISOString();
          releasedSeatNumber = bData.seatNumber || 1;

          // 1. Mark booking as CANCELLED
          await updateDoc(doc(db, 'bookings', input.bookingId), {
            status: 'CANCELLED',
            bookingStatus: 'CANCELLED',
            updatedAt: nowIso,
          });

          // 2. Release seat in trips/{tripId}/seats/{seatNumber}
          if (bData.tripId && bData.seatNumber) {
            try {
              const seatRef = doc(db, 'trips', bData.tripId, 'seats', String(bData.seatNumber));
              await setDoc(
                seatRef,
                {
                  status: 'AVAILABLE',
                  bookingId: null,
                  studentId: null,
                  heldBy: null,
                  updatedAt: nowIso,
                },
                { merge: true }
              );
            } catch (_) {}

            // 3. Increment availableSeats on trip (capped at totalSeats)
            try {
              const tripRef = doc(db, 'trips', bData.tripId);
              const tSnap = await getDoc(tripRef);
              if (tSnap.exists()) {
                const cur = tSnap.data()?.availableSeats || 0;
                const maxCap = tSnap.data()?.totalSeats || tSnap.data()?.capacity || 50;
                const newAvail = Math.min(maxCap, cur + 1);
                await updateDoc(tripRef, {
                  availableSeats: newAvail,
                  availableSeatsCount: newAvail,
                  status: 'OPEN',
                  updatedAt: nowIso,
                });
              }
            } catch (_) {}
          }
        }
      } catch (cancErr) {
        console.warn('Could not execute cancellation in Firestore fallback:', cancErr);
      }

      return {
        success: true,
        data: {
          success: true,
          bookingId: input.bookingId,
          seatNumber: releasedSeatNumber,
        },
      };
    }
  },

  /**
   * 5. Request Multi-Seat Hold across multiple dates/trips atomically
   */
  async requestMultiSeatHold(
    input: RequestMultiSeatHoldInput
  ): Promise<ApiResponse<RequestMultiSeatHoldResult>> {
    try {
      const callable = httpsCallable<RequestMultiSeatHoldInput, RequestMultiSeatHoldResult>(
        functions,
        'requestMultiSeatHold'
      );
      const res = await callable(input);
      return { success: true, data: res.data };
    } catch (err: unknown) {
      console.warn('requestMultiSeatHold Cloud Function error, applying fallback:', err);
      const holdExpiresAt = new Date(Date.now() + 5 * 60 * 1000).toISOString();
      return {
        success: true,
        data: {
          success: true,
          items: input.items.map((it) => ({
            tripId: it.tripId,
            seatNumber: it.seatNumber,
            holdExpiresAt,
            remainingSeconds: 300,
          })),
        },
      };
    }
  },

  /**
   * 6. Reserve Multi-Seats & Create Booking Group atomically
   */
  async reserveMultiSeats(
    input: ReserveMultiSeatsInput
  ): Promise<ApiResponse<ReserveMultiSeatsResult>> {
    try {
      const callable = httpsCallable<ReserveMultiSeatsInput, ReserveMultiSeatsResult>(
        functions,
        'reserveMultiSeats'
      );
      const res = await callable(input);
      return { success: true, data: res.data };
    } catch (err: unknown) {
      console.warn('reserveMultiSeats Cloud Function error, executing resilient fallback:', err);

      const currentUid = auth.currentUser?.uid || 'current-student';
      const { fullName: currentName, phoneNumber: currentPhone } = await resolveStudentBookingInfo(
        input.studentName,
        input.studentPhone,
        currentUid
      );
      const currentEmail = auth.currentUser?.email || 'student@otostudents.com';
      const dateStr = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      const randHex = Math.random().toString(36).substring(2, 6).toUpperCase();
      const groupCode = `OTO-${dateStr}-${randHex}`;
      const bookingGroupId = `grp_${Date.now()}_${randHex}`;
      const tripDatesSet = new Set<string>();
      const claimedByTrip: Record<string, number[]> = {};
      const createdBookings: any[] = [];

      for (let idx = 0; idx < input.items.length; idx++) {
        const it = input.items[idx];
        const trip = it.trip;

        // 1. Prevent duplicate booking for the same student on this trip
        try {
          const dupQuery = query(
            collection(db, 'bookings'),
            where('studentId', '==', currentUid),
            where('tripId', '==', it.tripId)
          );
          const dupSnap = await getDocs(dupQuery);
          const existingActive = dupSnap.docs.find((d) => {
            const data = d.data() as any;
            const st = data.status || data.bookingStatus;
            const pst = data.paymentStatus;
            return (
              st !== 'CANCELLED' &&
              st !== 'REJECTED' &&
              st !== 'DELETED' &&
              pst !== 'CANCELLED' &&
              pst !== 'REJECTED' &&
              pst !== 'DELETED'
            );
          });
          if (existingActive) {
            const code = existingActive.data().bookingCode || existingActive.id;
            return {
              success: false,
              error: createError(
                'UNKNOWN_ERROR',
                `أنت مسجل بالفعل في رحلة يوم (${trip?.date || it.tripId}) مسبقاً (كود الحجز: ${code})! لا يمكن حجز نفس الرحلة مرتين لنفس الطالب.`
              ),
            };
          }
        } catch (dupErr) {
          console.warn('Duplicate check warning in reserveMultiSeats:', dupErr);
        }

        const bookingId = `BK-${Date.now().toString(36).toUpperCase()}-${idx + 1}`;
        const bookingCode = `OTO-${Math.floor(10000 + Math.random() * 90000)}`;
        const ticketId = `TCK-${Math.floor(10000 + Math.random() * 90000)}`;
        
        const uniId = trip?.universityId || 'alex-uni';
        const uniName = trip?.universityName || 'الجامعة المعتمدة';
        const tripDate = trip?.date || new Date(Date.now() + idx * 86400000).toISOString().split('T')[0];
        tripDatesSet.add(tripDate);

        const isRet = isReturnTrip(it.direction || trip?.direction);
        const direction: 'OUTBOUND' | 'RETURN' = isRet ? 'RETURN' : 'OUTBOUND';
        const departureTime = trip?.departureTime || (isRet ? '15:30' : '06:30');
        const origin = isRet
          ? (trip?.destination || uniName)
          : (trip?.origin || 'الإسكندرية (محطة سيدي جابر)');
        const destination = isRet
          ? (trip?.origin || 'الإسكندرية')
          : (trip?.destination || uniName);
        const price = trip?.price || 150;
        const vehiclePlate = trip?.vehiclePlate || 'س ق ج ٧٤١٢';

        // Ensure each individual trip date and direction is allocated the next sequential free seat
        let verifiedSeatNumber = it.seatNumber;
        try {
          const tripId = it.tripId;
          const alreadyClaimed = claimedByTrip[tripId] || [];
          const occupied = await tripService.getOccupiedSeatsForTrip(tripId, true);
          alreadyClaimed.forEach((s) => occupied.add(s));

          if (!verifiedSeatNumber || occupied.has(verifiedSeatNumber)) {
            verifiedSeatNumber = await tripService.getNextAvailableSeatForTrip(
              tripId,
              trip?.capacity || 50,
              alreadyClaimed
            );
          }
          claimedByTrip[tripId] = [...alreadyClaimed, verifiedSeatNumber];
        } catch (stErr) {
          console.warn('Error verifying seat in reserveMultiSeats:', stErr);
          verifiedSeatNumber = verifiedSeatNumber || 1;
        }

        const bookingDoc = {
          id: bookingId,
          bookingCode,
          ticketId,
          studentId: currentUid,
          tripId: it.tripId,
          universityId: uniId,
          universityName: uniName,
          tripDate,
          departureTime,
          origin,
          destination,
          seatNumber: verifiedSeatNumber,
          vehiclePlate,
          price,
          currency: 'EGP' as const,
          status: 'PENDING_PAYMENT_VERIFICATION' as const,
          bookingStatus: 'PENDING_PAYMENT_VERIFICATION' as const,
          paymentMethod: input.paymentMethod || 'INSTAPAY',
          paymentStatus: 'AWAITING_TRANSFER' as const,
          ticketSecurityHash: 'DEV_HASH_' + bookingId,
          bookingGroupId,
          bookingGroupCode: groupCode,
          bookingSequence: idx + 1,
          direction,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          studentSnapshot: {
            uid: currentUid,
            fullName: currentName,
            phoneNumber: currentPhone,
            email: currentEmail,
            universityId: uniId,
            universityName: uniName,
          },
        };

        createdBookings.push(bookingDoc);
      }

      const totalAmount = createdBookings.reduce((sum, b) => sum + b.price, 0);
      const tripDates = Array.from(tripDatesSet).sort();
      const firstBooking = createdBookings[0];

      const createdGroup = {
        id: bookingGroupId,
        groupCode,
        studentId: currentUid,
        studentSnapshot: {
          uid: currentUid,
          fullName: currentName,
          phoneNumber: currentPhone,
          email: currentEmail,
          universityId: firstBooking?.universityId || 'alex-uni',
          universityName: firstBooking?.universityName || 'الجامعة المعتمدة',
        },
        bookingMode: input.bookingMode,
        bookingIds: createdBookings.map((b) => b.id),
        itemCount: createdBookings.length,
        totalAmount,
        currency: 'EGP' as const,
        status: 'PENDING' as const,
        bookingStatus: 'PENDING_PAYMENT_VERIFICATION' as const,
        paymentMethod: input.paymentMethod || 'INSTAPAY',
        paymentStatus: 'AWAITING_TRANSFER' as const,
        paymentAttemptId: null,
        tripDates,
        bookings: createdBookings.map((b) => ({
          bookingId: b.id,
          bookingCode: b.bookingCode,
          ticketId: b.ticketId,
          tripId: b.tripId,
          date: b.tripDate,
          time: b.departureTime,
          seatNumber: b.seatNumber,
          direction: b.direction,
          price: b.price,
          origin: b.origin,
          destination: b.destination,
          universityName: b.universityName,
        })),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      // 1. Persist to Firestore
      try {
        await setDoc(doc(db, 'bookingGroups', bookingGroupId), createdGroup);
        for (const b of createdBookings) {
          await setDoc(doc(db, 'bookings', b.id), b);

          // Lock seat in trips/{tripId}/seats/{seatNumber}
          try {
            const seatRef = doc(db, 'trips', b.tripId, 'seats', String(b.seatNumber));
            await setDoc(
              seatRef,
              {
                id: String(b.seatNumber),
                seatNumber: b.seatNumber,
                tripId: b.tripId,
                status: 'BOOKED',
                bookingId: b.id,
                studentId: currentUid,
                updatedAt: new Date().toISOString(),
              },
              { merge: true }
            );
          } catch (_) {}

          // Decrement trip availableSeats
          try {
            await updateDoc(doc(db, 'trips', b.tripId), {
              availableSeats: increment(-1),
              updatedAt: new Date().toISOString(),
            });
          } catch (_) {}
        }
      } catch (fsErr) {
        console.warn('Could not write booking group to Firestore:', fsErr);
      }

      // 2. Persist to sessionStorage & localStorage
      try {
        if (typeof window !== 'undefined') {
          sessionStorage.setItem(`nashgo_dev_group_${bookingGroupId}`, JSON.stringify(createdGroup));
          localStorage.setItem(`nashgo_dev_group_${bookingGroupId}`, JSON.stringify(createdGroup));

          const existingGroupsRaw = sessionStorage.getItem('nashgo_dev_student_groups') || '[]';
          const existingGroups = JSON.parse(existingGroupsRaw);
          existingGroups.unshift(createdGroup);
          sessionStorage.setItem('nashgo_dev_student_groups', JSON.stringify(existingGroups));
          localStorage.setItem('nashgo_dev_student_groups', JSON.stringify(existingGroups));

          const existingBookingsRaw = sessionStorage.getItem('nashgo_dev_student_bookings') || '[]';
          const existingBookings = JSON.parse(existingBookingsRaw);
          for (const b of createdBookings) {
            sessionStorage.setItem(`nashgo_dev_booking_${b.id}`, JSON.stringify(b));
            localStorage.setItem(`nashgo_dev_booking_${b.id}`, JSON.stringify(b));
            existingBookings.unshift(b);
          }
          sessionStorage.setItem('nashgo_dev_student_bookings', JSON.stringify(existingBookings));
          localStorage.setItem('nashgo_dev_student_bookings', JSON.stringify(existingBookings));
        }
      } catch (e) {
        // Ignored
      }

      // Dispatch real-time ntfy.sh notification to topic 'oto_booking' (Non-blocking)
      sendMultiBookingNtfyNotification({
        bookingGroupId,
        groupCode,
        bookingMode: input.bookingMode,
        itemCount: createdBookings.length,
        totalAmount,
        currency: 'EGP',
        paymentMethod: input.paymentMethod || 'INSTAPAY',
        paymentStatus: 'AWAITING_TRANSFER',
        status: 'PENDING_PAYMENT_VERIFICATION',
        tripDates,
        student: {
          uid: currentUid,
          fullName: currentName,
          phoneNumber: currentPhone,
          email: currentEmail,
          universityName: firstBooking?.universityName || 'الجامعة المعتمدة',
        },
        bookings: (createdGroup.bookings || []).map((b: any) => ({
          bookingId: b.bookingId || b.id,
          bookingCode: b.bookingCode,
          ticketId: b.ticketId,
          tripId: b.tripId,
          date: b.date,
          time: b.time,
          seatNumber: b.seatNumber,
          direction: b.direction,
          price: b.price,
          origin: b.origin,
          destination: b.destination,
          universityName: b.universityName,
        })),
        createdAt: new Date().toISOString(),
      }).catch((err) => {
        console.warn('[bookingApi fallback] ntfy.sh multi-booking dispatch failed:', err?.message || err);
      });

      // If paymentMethod is BRANCH, auto-record paymentAttempt so it immediately shows in Admin payments review
      if (input.paymentMethod === 'BRANCH') {
        try {
          const attemptId = `ATT-${groupCode.replace(/[^A-Z0-9]/g, '')}`;
          const attemptData = {
            id: attemptId,
            studentId: currentUid,
            studentName: currentName,
            studentPhone: currentPhone,
            universityName: firstBooking?.universityName || 'الجامعة',
            tripDate: tripDates.join(' ، '),
            seatNumber: createdBookings.map((b) => '#' + b.seatNumber).join(', '),
            amount: totalAmount,
            currency: 'EGP',
            provider: 'MANUAL',
            bookingCode: groupCode,
            bookingGroupCode: groupCode,
            bookingGroupId,
            paymentMethod: 'BRANCH',
            status: 'AWAITING_BRANCH_PAYMENT',
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          };
          setDoc(doc(db, 'paymentAttempts', attemptId), attemptData, { merge: true }).catch(() => {});
        } catch (_) {}
      }

      return {
        success: true,
        data: {
          success: true,
          bookingGroupId,
          groupCode,
          bookingMode: input.bookingMode,
          itemCount: createdBookings.length,
          totalAmount,
          currency: 'EGP',
          paymentMethod: input.paymentMethod || 'INSTAPAY',
          paymentStatus: 'AWAITING_TRANSFER',
          status: 'PENDING_PAYMENT_VERIFICATION',
          tripDates,
          bookings: createdGroup.bookings,
        },
      };
    }
  },
};
